import { useState, useCallback } from 'react';
import postRequestWithLocalStorage from '../api/postRequestWithLocalStorageHook';
import { updateSlideTaskId } from '../api/slides';

/**
 * Кастомный хук для обработки слайдов и сохранения task_id
 *
 * @returns {Object} - Объект с функцией processSlide и состоянием isProcessing
 */
export const useProcessTask = () => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Отправляет слайд на обработку и сохраняет полученный task_id в базе данных
     *
     * @param {number} slideId - ID слайда
     * @param {Object} processData - Данные для отправки на обработку
     * @param {string} processUrl - URL эндпойнта для обработки
     * @param {number} maxRetries - Максимальное количество попыток (по умолчанию 3)
     * @param {number} retryDelay - Задержка между попытками в мс (по умолчанию 2000)
     * @returns {Promise} - Результат обработки
     */
    const processSlide = useCallback(async (slideId, processData, processUrl) => {
        setIsProcessing(true);
        setError(null);

        try {

            // Отправляем запрос на разметку
            const processResponse = await postRequestWithLocalStorage(processUrl, processData);

            let taskId = null;

            if (processResponse?.data?.task_id) {
                taskId = processResponse.data.task_id;
            }

            // Если получили task_id, сохраняем его в БД
            if (taskId && slideId) {
                console.log(`💾 Saving task_id ${taskId} for slide ${slideId}`);

                const updateResponse = await updateSlideTaskId(slideId, taskId);

                console.log(`✅ Successfully linked task ${taskId} to slide ${slideId}`, updateResponse);

                return {
                    success: true,
                    taskId: taskId,
                    processResponse,
                    updateResponse
                };
            } else {
                return {
                    success: false,
                    error: 'No task_id received from process request',
                    processResponse,
                    debug: {
                        hasData: !!processResponse?.data,
                        dataKeys: Object.keys(processResponse?.data || {}),
                        slideId: slideId,
                        slideIdType: typeof slideId
                    }
                };
            }

        } catch (error) {
            setError(error);

            return {
                success: false,
                error: error.message || 'Unknown error during processing',
                errorDetails: error
            };
        } finally {
            setIsProcessing(false);
        }
    }, []);

    /**
     * Сброс состояния ошибки
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        processSlide,
        isProcessing,
        error,
        clearError
    };
};
